/**
 * Analytics Tracking Utility
 * Track key user events for analytics dashboard
 */

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * Track an event
 * @param {string} eventType - Event type (e.g., 'age_selector_click', 'filter_applied')
 * @param {object} properties - Event properties
 * @param {string} userId - User ID (optional)
 */
export const trackEvent = async (eventType, properties = {}, userId = null) => {
  try {
    const sessionId = getSessionId();
    
    await fetch(`${BACKEND_URL}/api/events`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({
        event_type: eventType,
        user_id: userId,
        session_id: sessionId,
        properties
      })
    });
  } catch (error) {
    console.error('Failed to track event:', error);
  }
};

/**
 * Get or create session ID
 * @returns {string} Session ID
 */
const getSessionId = () => {
  let sessionId = sessionStorage.getItem('session_id');
  if (!sessionId) {
    sessionId = Math.random().toString(36).substring(2, 15);
    sessionStorage.setItem('session_id', sessionId);
  }
  return sessionId;
};

/**
 * Track age selector click
 * @param {string} ageGroup - Age group clicked (0-2, 3-5, 6-12)
 */
export const trackAgeSelectorClick = (ageGroup) => {
  trackEvent('age_selector_click', { age_group: ageGroup });
};

/**
 * Track filter applied
 * @param {string} filterType - Filter type (price, age, category, feature)
 * @param {any} filterValue - Filter value
 */
export const trackFilterApplied = (filterType, filterValue) => {
  trackEvent('filter_applied', {
    filter_type: filterType,
    filter_value: filterValue
  });
};

/**
 * Track bundle view
 * @param {string} bundleId - Bundle ID
 * @param {string} bundleName - Bundle name
 */
export const trackBundleView = (bundleId, bundleName) => {
  trackEvent('bundle_viewed', {
    bundle_id: bundleId,
    bundle_name: bundleName
  });
};

/**
 * Track bundle add to cart
 * @param {string} bundleId - Bundle ID
 * @param {string} bundleName - Bundle name
 * @param {number} price - Bundle price
 */
export const trackBundleAddToCart = (bundleId, bundleName, price) => {
  trackEvent('bundle_added_to_cart', {
    bundle_id: bundleId,
    bundle_name: bundleName,
    price
  });
};

/**
 * Track wishlist add
 * @param {string} productId - Product ID
 */
export const trackWishlistAdd = (productId) => {
  trackEvent('wishlist_add', { product_id: productId });
};

/**
 * Track wishlist share
 */
export const trackWishlistShare = () => {
  trackEvent('wishlist_shared');
};

/**
 * Track cart share via WhatsApp
 * @param {number} itemCount - Number of items in cart
 * @param {number} total - Cart total
 */
export const trackCartShare = (itemCount, total) => {
  trackEvent('cart_shared_whatsapp', {
    item_count: itemCount,
    cart_total: total
  });
};

/**
 * Track quick view open
 * @param {string} productId - Product ID
 */
export const trackQuickView = (productId) => {
  trackEvent('quick_view_opened', { product_id: productId });
};

/**
 * Track gift registry creation
 * @param {number} itemCount - Number of items in registry
 */
export const trackGiftRegistryCreate = (itemCount) => {
  trackEvent('gift_registry_created', { item_count: itemCount });
};
